import { useEffect, useState } from 'react';
import { apiClient } from '../utils/api';

interface ConnectionState {
  isOnline: boolean;
  lastError: Error | null;
  retryCount: number;
}

export const useConnectionStatus = () => {
  const [connectionState, setConnectionState] = useState<ConnectionState>({
    isOnline: true,
    lastError: null,
    retryCount: 0
  });

  const [isCheckingConnection, setIsCheckingConnection] = useState(false);

  const checkConnection = async () => {
    setIsCheckingConnection(true);
    try {
      // Make a simple health check request
      await apiClient.getHubSpotAuthUrl();
      setConnectionState(apiClient.getConnectionState());
    } catch (error) {
      setConnectionState(apiClient.getConnectionState());
    } finally {
      setIsCheckingConnection(false);
    }
  };

  const resetConnection = () => {
    apiClient.resetConnectionState();
    setConnectionState(apiClient.getConnectionState());
  };

  // Update connection state periodically
  useEffect(() => {
    const interval = setInterval(() => {
      setConnectionState(apiClient.getConnectionState());
    }, 5000); // Check every 5 seconds

    return () => clearInterval(interval);
  }, []);

  // Listen for online/offline events
  useEffect(() => {
    const handleOnline = () => {
      resetConnection();
    };

    const handleOffline = () => {
      setConnectionState({
        isOnline: false,
        lastError: new Error('No internet connection'),
        retryCount: 0
      });
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return {
    connectionState,
    isCheckingConnection,
    checkConnection,
    resetConnection,
    isOnline: connectionState.isOnline,
    hasConnectionError: !connectionState.isOnline && connectionState.lastError !== null,
    retryCount: connectionState.retryCount
  };
};
